import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, FileSpreadsheet, Download, Loader2, TrendingUp, Clock, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectReportsTab = ({ projectId, projectName }) => {
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      const token = localStorage.getItem('token');
      const urls = {
        pdf: `${API_URL}/api/projects/${projectId}/reports/pdf`,
        excel: `${API_URL}/api/projects/${projectId}/reports/excel`,
        profitability: `${API_URL}/api/projects/reports/profitability`,
      };
      const res = await fetch(urls[type], { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Erreur export');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const names = {
        pdf: `rapport_${(projectName || 'projet').replace(/\s/g, '_').slice(0, 30)}.pdf`,
        excel: `temps_${(projectName || 'projet').replace(/\s/g, '_').slice(0, 30)}.xlsx`,
        profitability: 'rentabilite_projets.xlsx',
      };
      a.download = names[type];
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Telecharge !');
    } catch (e) {
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloading(null);
    }
  };

  const reports = [
    {
      id: 'pdf',
      icon: FileText,
      title: 'Rapport projet PDF',
      description: 'Resume complet : finances, taches, temps, ventilation mensuelle. Ideal pour les clients et la direction.',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-100',
    },
    {
      id: 'excel',
      icon: FileSpreadsheet,
      title: 'Export temps & taches (Excel)',
      description: '3 onglets : entrees de temps, taches avec heures estimees/reelles, charge de travail par collaborateur.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-100',
    },
    {
      id: 'profitability',
      icon: TrendingUp,
      title: 'Rentabilite tous projets (Excel)',
      description: 'Analyse de rentabilite de tous vos projets avec ventilation par client. Vue globale de votre portfolio.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-100',
    },
  ];

  return (
    <div className="space-y-4" data-testid="reports-tab">
      <div className="grid md:grid-cols-3 gap-4">
        {reports.map(r => {
          const Icon = r.icon;
          const isLoading = downloading === r.id;
          return (
            <Card key={r.id} className={`border ${r.borderColor} hover:shadow-md transition-shadow`} data-testid={`report-card-${r.id}`}>
              <CardContent className="p-5 flex flex-col h-full">
                <div className={`w-10 h-10 rounded-lg ${r.bgColor} flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${r.color}`} />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">{r.title}</h3>
                <p className="text-xs text-slate-500 mb-4 flex-1">{r.description}</p>
                <Button
                  onClick={() => handleDownload(r.id)}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full"
                  data-testid={`download-${r.id}-btn`}
                >
                  {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Telecharger
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <BarChart3 className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-slate-700">A propos des rapports</h4>
              <ul className="text-xs text-slate-500 mt-1 space-y-0.5 list-disc list-inside">
                <li>Le rapport PDF inclut le resume financier, la liste des taches et le detail du temps</li>
                <li>L'export Excel contient 3 feuilles : temps, taches (avec heures estimees vs reelles), et charge par collaborateur</li>
                <li>Le rapport de rentabilite couvre tous vos projets avec une ventilation par client</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectReportsTab;
